import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Settings } from "@/interface/entityInterface";
import axiosInstance from "@/utils/axiosInstance";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { toast } from "sonner";

interface ConfigProps {
  assign_order_by_driver: boolean;
  show_phone_on_driver: boolean;
  completed_orders: boolean;
}

interface Props {
  configs: ConfigProps;
  setConfigs: Dispatch<SetStateAction<ConfigProps>>
  label: string;
  description: string;
  configProp: 'assign_order_by_driver' | 'show_phone_on_driver' | 'completed_orders'
}

const ConfigItem = ({configs, setConfigs, label, description, configProp}: Props) => (
  <div className="flex items-center justify-between">
    <div className="space-y-1">
      <p className="text-sm font-medium leading-none">{label}</p>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        {description}
      </p>
    </div>
    <Switch
      id={configProp}
      onClick={() =>
        setConfigs((prev) => ({
          ...prev,
          [configProp]: !prev[configProp],
        }))
      }
      checked={configs[configProp]}
    />
  </div>
);

const OrdersSettings = ({ settings }: { settings: Settings | null }) => {
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState<ConfigProps>({
    assign_order_by_driver: false,
    show_phone_on_driver: false,
    completed_orders: false
  });

  useEffect(() => {
    if (!settings) return;
    const { show_driver_on_order_assign, show_phone_number_to_driver, completed_orders } =
      settings;
    setConfig({
      assign_order_by_driver: show_driver_on_order_assign || false,
      show_phone_on_driver: show_phone_number_to_driver || false,
      completed_orders: completed_orders || false
    });
  }, [settings]);

  const submitSettings = async () => {
    setLoading(true);
    try {
      await axiosInstance.put(`/settings/${settings?.id}`, {
        show_driver_on_order_assign: config.assign_order_by_driver,
        show_phone_number_to_driver: config.show_phone_on_driver,
        show_vehicle_on_order_assign: config.assign_order_by_driver
          ? false
          : true,
        completed_order: config.completed_orders
      });

      toast.success("Changes updated.");
    } catch (err: any) {
      toast.error(err.response.data.message);
    } finally {
      setLoading(false);
    }
  };

  if (!settings) return;

  return (
    <Card className="md:w-1/2">
      <CardHeader>
        <CardTitle>Orders</CardTitle>
        <CardDescription>Manage your orders settings.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <ConfigItem 
          label="Assign by driver"
          description="Able to assign an order to a driver."
          configs={config}
          setConfigs={setConfig}
          configProp="assign_order_by_driver"
        />
        <ConfigItem 
          label="Allow phone number"
          description="Toggle this for drivers to see phone number for each order."
          configs={config}
          setConfigs={setConfig}
          configProp="show_phone_on_driver"
        />
        <ConfigItem 
          label="Allow completed orders"
          description="Toggle this for drivers to see their completed orders history."
          configs={config}
          setConfigs={setConfig}
          configProp="completed_orders"
        />
        <div className="flex justify-end">
          <Button onClick={submitSettings} disabled={loading}>
            {loading ? "Saving..." : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default OrdersSettings;
